<script setup>
import LoadingSvg from "@/components/UI/LoadingSvg.vue";
import {computed, ref} from "vue";
import { createToaster } from "@meforma/vue-toaster";
import { Api } from "@/service/api";
import { useRoute } from 'vue-router'
import stringUtils from "@/service/stringUtils";


const route = useRoute()

const toaster = createToaster({ /* options */ });

const showPassword = ref(false)
const showRepeatPassword = ref(false)
const isLoading = ref(false)

const password = ref("")
const repeatPassword = ref("")

const step = ref("password_change")


function togglePassword() {
    showPassword.value = !showPassword.value
}

function toggleRepeatPassword() {
    showRepeatPassword.value = !showRepeatPassword.value
}

async function savePassword() {
    isLoading.value = true
    const resetHash = route.query.token

    if (password.value !== repeatPassword.value) {
        toaster.error("Пароли не совпадают")
        isLoading.value = false
        return
    }

    if (!isAllRulesTrue.value) {
        toaster.error("Выполнены не все условия пароля")
        isLoading.value = false
        return
    }

    if (resetHash === "" || resetHash === undefined || resetHash === null) {
        toaster.error("Token has expired")
        isLoading.value = false
        return
    }

    const response = await Api.resetPassword(resetHash, password.value)

    if (response.status === "error") {
        toaster.error(response.message)
        isLoading.value = false
        return
    }

    step.value = "success_password_change"
}

const isPasswordHasCapital = computed(() => {
    return stringUtils.hasCapital(password.value)
})

const isPasswordHasLowerCase = computed(() => {
    return stringUtils.hasLowerCase(password.value)
})

const isPasswordHasDigits = computed(() => {
    return stringUtils.hasDigits(password.value)
})

const isPasswordHasSpecialChars = computed(() => {
    return stringUtils.hasSpecialChars(password.value)
})

const isPasswordLengthValid = computed(() => {
    return (password.value.length >= 8) && (password.value.length <= 32)
})

const isAllRulesTrue = computed(() => {
    return (
        isPasswordHasCapital.value &&
        isPasswordHasLowerCase.value &&
        isPasswordHasDigits.value &&
        isPasswordHasSpecialChars.value &&
        isPasswordLengthValid.value
    )
})

</script>

<template>
    <div class="w-full h-screen flex items-center justify-center">
        <div
            class="flex rounded-2xl bg-white justify-center items-center shadow-xl h-[400px] w-[340px]
                   sm:w-[400px]
                   xl:w-[450px]
             "
            v-if="step === 'password_change'"
        >
            <div class="flex w-3/4 flex-col">
                <div class="text-lg font-bold">Введите новый пароль</div>
                <div
                    class="flex flex-col"
                >
                    <div class="w-full relative mt-5">
                        <input
                            :type="showPassword ? 'text' : 'password'"
                            id="password"
                            v-model="password"
                            class="h-10 w-full px-3 pt-1 text-xs border-2 border-transparent rounded-md bg-light-blue outline-none focus:outline-none focus:border-blue-600 password-criterias"
                        >
                        <label class="absolute top-1 left-3 transparent small-text-label select-none">Пароль</label>
                        <!-- svg глазика для того чтобы скрыть пароль -->
                        <div v-if="!showPassword" class="absolute right-3 top-3 cursor-pointer" v-on:click="togglePassword">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <!-- svg глазика для того чтобы показать пароль -->
                        <div v-else class="absolute right-3 top-3 cursor-pointer" v-on:click="togglePassword">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                                <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                                <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                            </svg>
                        </div>
                    </div>
                    <div class="w-full relative mt-5">
                        <input
                            :type="showRepeatPassword ? 'text' : 'password'"
                            id="repeat-password"
                            v-model="repeatPassword"
                            class="h-10 w-full px-3 pt-1 text-xs border-2 border-transparent rounded-md bg-light-blue outline-none focus:outline-none focus:border-blue-600 password-criterias"
                        >
                        <label class="absolute top-1 left-3 transparent small-text-label select-none">Повторите пароль</label>
                        <div v-if="!showRepeatPassword" class="absolute right-3 top-3 cursor-pointer" v-on:click="toggleRepeatPassword">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <!-- svg глазика для того чтобы показать пароль -->
                        <div v-else class="absolute right-3 top-3 cursor-pointer" v-on:click="toggleRepeatPassword">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-gray-500">
                                <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                                <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                                <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                            </svg>
                        </div>
                    </div>
                    <div class="mt-2">
                        <div class="text-xs font-bold">
                            Требования к паролю:
                        </div>
                        <div class="text-xs">
                            <div :class="{'text-green-600': isPasswordHasCapital && isPasswordHasLowerCase, 'text-red-500': !isPasswordHasCapital || !isPasswordHasLowerCase}">
                                • прописные и строчные буквы латинского алфавита (A-Z)
                            </div>
                            <div :class="{'text-green-600': isPasswordHasDigits, 'text-red-500': !isPasswordHasDigits}">
                                • хотя бы одна цифра (0-9)
                            </div>
                            <div :class="{'text-green-600': isPasswordHasSpecialChars, 'text-red-500': !isPasswordHasSpecialChars}">
                                • хотя бы один спецсимвол (!@#$% и др.)
                            </div>
                            <div :class="{'text-green-600': isPasswordLengthValid, 'text-red-500': !isPasswordLengthValid}">
                                • длина от 8 до 48 символов включительно
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-col items-center">
                        <div class="flex items-center justify-center h-10 w-full
                                 rounded-md mt-5
                                text-white font-bold text-sm cursor-pointer
                                bg-blue-600 hover:bg-blue-700
                             "
                             v-on:click="savePassword"
                             v-if="!isLoading"
                        >
                            Сохранить
                        </div>
                        <div class="flex items-center justify-center h-10 w-full
                                outline-none rounded-md mt-5
                                text-white font-bold text-sm
                                bg-blue-700
                             "
                             v-else
                        >
                            <LoadingSvg class="size-6 animate-spin"/>
                        </div>
                        <div class="w-full flex justify-between mt-4 px-2">
                            <router-link class="font-bold text-blue-600 hover:text-blue-700" to="/register">Регистрация</router-link>
                            <router-link class="font-bold text-blue-600 hover:text-blue-700" to="/login">Войти</router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="flex rounded-2xl bg-white justify-center items-center shadow-xl h-[200px] w-[450px]
                   xl:w-[450px]
             "
            v-if="step === 'success_password_change'"
        >
            <div class="flex w-3/4 flex-col">
                <div class="text-lg font-bold">Ваш пароль был успешно изменён</div>
                <router-link to="/login" class="font-bold text-sm text-blue-500 hover:text-blue-600 mt-5">
                    Вернуться на страницу входа
                </router-link>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bg-light-blue {
    background-color: #dee8f5;
}
.small-text-label {
    font-size: 0.5rem
}
.password-criterias-display{
    display: none;
}
.password-criterias:focus ~ .password-criterias-display{
    display: flex;
}
.arrow-down {
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;

    border-left: 8px solid #dee8f5;
}
.bg-light-block {
    background: #dee8f5;
}
.checkbox {
    appearance: none;
    background-color: #dee8f5;;
    margin: 0;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    display: grid;
    place-content: center;
    user-select: none;
    cursor: pointer;
}
.checkbox::before {
    content: "";
    width: 7px;
    height: 7px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em #0A84FF;
}
.checkbox:checked::before {
    transform: scale(1);
}
.checkbox:checked {
    border-color: #0A84FF;
}
</style>